/**
 * One recording on a client's movement, as every surface of this app reads it.
 *
 * The author is a person reference, never a role: the client films most recordings, and a trainer
 * running a session can film one too (R25). There is no role on the wire.
 */

import { calendarDaysSince } from '~/shared/lib/calendarDate';
import { dateTimeFormat, numberFormat, relativeTimeFormat } from '~/shared/lib/intlCache';

export interface ClipPerson {
  readonly userId: string | null;
  readonly fullName: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
}

export interface ClipComment {
  readonly id: number;
  readonly author: ClipPerson;
  readonly body: string;
  readonly createdAt: number;
  readonly editedAt: number | null;
  /** The moment of the film the comment is pinned to, or `null` for a remark about the whole clip. */
  readonly offsetMs: number | null;
}

export interface ClipAnchor {
  readonly workoutExecutionId: number;
  readonly setId: number | null;
  readonly setNumber: number | null;
  readonly weightKg: number | null;
  readonly reps: number | null;
}

export interface ClipMedia {
  readonly width: number | null;
  readonly height: number | null;
  readonly durationSeconds: number | null;
  readonly posterUrl: string | null;
  readonly videoUrl: string | null;
}

export interface ClipEntry {
  readonly id: number;
  /** The client whose timeline holds the clip. `null` only when the read omits it. */
  readonly customerId: number | null;
  readonly exerciseId: number;
  /** The civil day the movement was performed, as the reader's LOCAL midnight of that date. */
  readonly performedOn: number;
  readonly createdAt: number;
  readonly author: ClipPerson;
  readonly unread: boolean;
  readonly expiresSoon: boolean;
  readonly expiresAt: number | null;
  readonly anchor: ClipAnchor | null;
  readonly media: ClipMedia;
  /** Oldest first, the newest page of the thread. */
  readonly comments: readonly ClipComment[];
}

/** R6: a movement's timeline holds at most ten recordings, flat across both authors. */
export const CLIPS_PER_EXERCISE = 10;

/** Newest first: the day performed, then the upload, then the id. */
export function byNewestClip(left: ClipEntry, right: ClipEntry): number {
  return (
    right.performedOn - left.performedOn || right.createdAt - left.createdAt || right.id - left.id
  );
}

/**
 * The clip as this client's card may use it. `?clip=` is an address the trainer can edit, so a clip
 * of another client reached on this client's card is treated exactly like one that no longer
 * exists: no film, no seen mark, no composer, and no movement chosen from it.
 */
export function clipOfClient(clip: ClipEntry | null | undefined, clientId: number): ClipEntry | null {
  if (clip === null || clip === undefined) return null;
  return clip.customerId === clientId ? clip : null;
}

/** The movement's newest recording, whatever the caller is looking at. */
export function newestClip(clips: readonly ClipEntry[]): ClipEntry | null {
  return [...clips].sort(byNewestClip)[0] ?? null;
}

/** Whether the signed-in viewer wrote this — the "Ty" label and the edit and delete controls. */
export function isViewer(person: ClipPerson, viewerUserId: string | null): boolean {
  return viewerUserId !== null && person.userId !== null && person.userId === viewerUserId;
}

export interface ClipPersonView {
  readonly name: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly isYou: boolean;
}

/** A person as a list or a thread names them: `you` for the signed-in viewer, the first name otherwise. */
export function clipPersonView(
  person: ClipPerson,
  viewerUserId: string | null,
  you: string,
): ClipPersonView {
  const isYou = isViewer(person, viewerUserId);
  return {
    name: isYou ? you : (person.fullName.split(' ')[0] ?? person.fullName),
    initials: person.initials,
    avatarUrl: person.avatarUrl,
    isYou,
  };
}

/** What a portrait phone films, and what a frame falls back to when the wire gave no shape. */
const PORTRAIT_ASPECT = 9 / 16;

/**
 * A clip's `width / height` for a frame's `aspect-ratio`, from the wire's own dimensions so the box
 * has its shape before the first frame arrives. Portrait when the wire gave none.
 */
export function clipAspect(media: {
  readonly width: number | null;
  readonly height: number | null;
}): number {
  const { width, height } = media;
  if (width === null || height === null || width <= 0 || height <= 0) return PORTRAIT_ASPECT;
  return width / height;
}

/**
 * "0:05,4" with tenths, "0:07" without — every clock a clip is read by: the player, its pins and
 * the queue card. Seconds are floored, so a card and the player never name different seconds.
 */
export function formatClock(seconds: number, tenths: boolean, locale: string): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const minutes = Math.floor(safe / 60);
  const rest = safe - minutes * 60;
  const whole = tenths ? Math.floor(rest * 10) / 10 : Math.floor(rest);
  const figure = numberFormat(locale, {
    minimumIntegerDigits: 2,
    minimumFractionDigits: tenths ? 1 : 0,
    maximumFractionDigits: tenths ? 1 : 0,
  }).format(whole);
  return `${String(minutes)}:${figure}`;
}

/** "dziś, 09:12", "wczoraj, 19:42", then "15 wrz" — a comment's age the way a thread reads it. */
export function commentWhen(at: number, now: number, locale: string): string {
  const days = calendarDaysSince(new Date(at), new Date(now));
  if (days === 0 || days === 1) {
    const word = relativeTimeFormat(locale, { numeric: 'auto' }).format(-days, 'day');
    const clock = dateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(at));
    return `${word}, ${clock}`;
  }
  return dateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(at));
}
